import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import OrderMobileCardShell from "../../Order/OrderMobileCardShell";
import ThemeAwareSpinner from "../../../../customComponents/ThemeAwareSpinner";

function CustomFilterDropdown({ options, value, onChange, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value) || options[0];

    return (
        <div className="agce-custom-select-container" ref={containerRef}>
            <div 
                className="agce-custom-select-trigger" 
                onClick={() => setIsOpen(!isOpen)}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{selectedOption ? selectedOption.label : label}</span>
                <i className={`ri-arrow-down-s-line ${isOpen ? 'open' : ''}`}></i>
            </div>
            {isOpen && (
                <ul className="agce-custom-select-options" role="listbox">
                    {options.map((o) => (
                        <li 
                            key={o.value || "all"} 
                            className={`agce-custom-select-option ${o.value === value ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(o.value);
                                setIsOpen(false);
                            }}
                            role="option"
                            aria-selected={o.value === value}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function TableHeaderSideSelect({ value, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const options = [
        { value: "All", label: "All", colorClass: "" },
        { value: "BUY", label: "Buy", colorClass: "text-success" },
        { value: "SELL", label: "Sell", colorClass: "text-danger" }
    ];

    const currentOpt = options.find((o) => o.value === value) || options[0];

    return (
        <div className="th-side-select-container" ref={containerRef}>
            <div
                className={`th-side-select-trigger cursor-pointer d-inline-flex align-items-center ${currentOpt.colorClass}`}
                onClick={() => setIsOpen(!isOpen)}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{currentOpt.label}</span>
                <i className={`ri-arrow-down-s-line th-side-select-arrow ${isOpen ? "open" : ""}`}></i>
            </div>
            {isOpen && (
                <ul className="th-side-select-menu" role="listbox">
                    {options.map((opt) => (
                        <li
                            key={opt.value}
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                            className={`th-side-select-item cursor-pointer d-flex align-items-center justify-content-between ${opt.colorClass} ${opt.value === value ? "active" : ""}`}
                            role="option"
                            aria-selected={opt.value === value}
                        >
                            <span>{opt.label}</span>
                            {opt.value === value && (
                                <i className="ri-check-line th-side-select-check ms-2"></i>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Open Orders — horizontal chip row with left/right arrows on small screens (matches OrdersSubTabs pattern). */
function FilterBarScrollable({ children }) {
    const [isPopupOpen, setIsPopupOpen] = React.useState(false);

    return (
        <div className="agce-margin-filter-container">
            {/* Desktop view: standard inline bar */}
            <div className="desktop_view2">
                <div className="d-flex align-items-center w-100 agce-margin-filter-bar spot_orders_filter_bar">
                    {children}
                </div>
            </div>

            {/* Mobile view: Filter toggle and popup */}
            <div className="agce-margin-mobile-filter-wrap">
                <div className="agce-margin-mobile-filter-header" style={{ padding: "10px 16px 0px" }}>
                    <button type="button" className="agce-margin-filter-toggle-btn" onClick={() => setIsPopupOpen(true)}>
                        <i className="ri-filter-3-line" /> Filter
                    </button>
                </div>

                {isPopupOpen && (
                    <div className="futures-margin-modal position-fixed w-100 h-100 top-0 start-0" style={{ zIndex: 9999, background: "rgba(0,0,0,0.5)" }} onClick={() => setIsPopupOpen(false)}>
                        <div className="cntnt bottom-margin-modal agce-margin-filter-popup position-absolute bottom-0 w-100" style={{ background: "var(--bg-primary, #fff)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header border-0 pb-0 pt-3 px-4">
                                <h6>Filters</h6>
                                <button type="button" className="btn-close" onClick={() => setIsPopupOpen(false)} />
                            </div>
                            <div className="modal-body p-4 agce-margin-filter-popup-content">
                                {children}
                            </div>
                            <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex">
                                <button type="button" className="btn btn-primary w-100 agce-margin-filter-apply-btn" onClick={() => setIsPopupOpen(false)}>
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function SpotOpenOrdersFilterRow({
    SPOT_OPEN_ORDER_KINDS,
    openOrderKindTab,
    setOpenOrderKindTab,
    orderType,
    setorderType,
}) {
    return (
        <FilterBarScrollable>
            <div className="spot_orders_filter_chips">
                {SPOT_OPEN_ORDER_KINDS.map((k) => (
                    <button
                        key={k.id}
                        type="button"
                        className={`spot_orders_chip ${openOrderKindTab === k.id ? "is-active" : ""}`}
                        onClick={() => setOpenOrderKindTab(k.id)}
                    >
                        {k.label}
                    </button>
                ))}

                <div className="spot_orders_filter_actions">
                    <CustomFilterDropdown
                        options={[
                            { value: "All", label: "All Sides" },
                            { value: "BUY", label: "Buy" },
                            { value: "SELL", label: "Sell" }
                        ]}
                        value={orderType}
                        onChange={(val) => setorderType(val)}
                        label="All Sides"
                    />
                    <button
                        type="button"
                        className="spot_orders_reset"
                        onClick={() => {
                            setOpenOrderKindTab("all");
                            setorderType("All");
                        }}
                    >
                        <i className="ri-refresh-line" aria-hidden="true" />
                        Reset
                    </button>
                </div>
            </div>
        </FilterBarScrollable>
    );
}

/** Primary tabs row (Open Orders / Order History / …) — arrows when list overflows on mobile */
function SpotOrdersPrimaryTabsScroll({ positionOrderTab, setPositionOrderTab }) {
    return (
        <div className="spot_orders_primary_tabs_scrollWrap">
            <ul className="position_list spot_orders_primary_tabs">
                <li className={`nav-item positions ${positionOrderTab === "positions" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("positions")}>
                        Open Orders
                    </button>
                </li>
                <li className={`nav-item orderHistory ${positionOrderTab === "orderHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("orderHistory")}>
                        Order History
                    </button>
                </li>
                <li className={`nav-item tradeHistory ${positionOrderTab === "tradeHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("tradeHistory")}>
                        Trade History
                    </button>
                </li>
            </ul>
        </div>
    );
}

const MARKET_OPEN_ORDER_HIDE_MS = 5000;

function isSpotPlainMarketOrder(item) {
    const t = String(item?.type || item?.order_type || "").toUpperCase();
    return t === "MARKET";
}

function orderHistoryLimitOrMarketPrice(item, safeToFixed8) {
    const raw = item?.price;
    if (raw != null && String(raw).trim() !== "") {
        const n = Number(raw);
        if (Number.isFinite(n)) return safeToFixed8(raw, "0");
    }
    const t = String(item?.type || item?.order_type || "").toUpperCase();
    if (t === "MARKET") return "Market";
    return "—";
}

function orderHistoryAvgFillPrice(item, safeToFixed8, filledQtyFn) {
    const filled = filledQtyFn(item);
    const raw = item?.avg_execution_price;
    if (filled <= 0 && (raw == null || String(raw).trim() === "" || Number(raw) === 0)) return "—";
    return safeToFixed8(raw, "—");
}

/** Spot order/tables BUY·SELL — dedicated classes (+ !important) beat .spot_orders_table tbody td. */
function spotSideColorClass(side) {
    const s = String(side ?? "").trim().toUpperCase();
    if (s === "BUY") return "agce-spot-side-buy";
    if (s === "SELL") return "agce-spot-side-sell";
    return "";
}

/** Hero subtitle (side + type/role) with buy/sell tint — matches profile `OrderMobileCardShell` rows. */
function mobileHeroSideTypeSubtitle(side, secondLabel) {
    const typeStr = secondLabel != null && String(secondLabel).trim() !== "" ? String(secondLabel).trim() : "";
    const parts = [side, typeStr].filter((x) => x && x !== "—");
    const line = parts.join(" · ");
    if (!line) return null;
    const s = String(side ?? "").toUpperCase();
    const tone = s === "BUY" ? "orders-mobCard__subtype--buy" : s === "SELL" ? "orders-mobCard__subtype--sell" : "";
    return <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line}</span>;
}

const TRADE_OPEN_ORDERS_DESKTOP_COLS = 13;
const TRADE_TRADE_HISTORY_DESKTOP_COLS = 9;
const TRADE_ORDER_HISTORY_DESKTOP_COLS = 15;

function formatExecutionLegDateTime(leg, format) {
    const ts = leg?.timestamp ?? leg?.executed_at ?? leg?.created_at;
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

/**
 * Combine multiple executions at the same trading price into one row
 * (aggregate quantity & fee); preserves first-seen price ordering.
 */
function aggregateOrderHistoryExecutedLegs(item) {
    const executedPrices = Array.isArray(item?.executed_prices) ? item.executed_prices : [];
    if (executedPrices.length === 0) return [];

    const priceGroupKey = (p) => {
        const n = Number(p);
        if (!Number.isFinite(n)) return `s:${String(p ?? "")}`;
        return `n:${n.toFixed(14)}`;
    };

    const keysInOrder = [];
    const buckets = new Map();

    executedPrices.forEach((trade) => {
        const gk = priceGroupKey(trade?.price);
        if (!buckets.has(gk)) {
            keysInOrder.push(gk);
            buckets.set(gk, {
                price: trade?.price,
                sumQty: 0,
                sumFee: 0,
                timestamp: trade?.timestamp ?? trade?.executed_at ?? null,
            });
        }
        const b = buckets.get(gk);
        b.sumQty += Number(trade?.quantity) || 0;
        b.sumFee += Number(trade?.fee) || 0;
        const legTs = trade?.timestamp ?? trade?.executed_at;
        if (legTs && (!b.timestamp || new Date(legTs) < new Date(b.timestamp))) {
            b.timestamp = legTs;
        }
    });

    return keysInOrder.map((gk, idx) => {
        const b = buckets.get(gk);
        return {
            reactKey: `agg:${gk}:${idx}`,
            price: b.price,
            quantity: b.sumQty,
            fee: b.sumFee,
            timestamp: b.timestamp,
        };
    });
}

const TradeHistorySection = (props) => {
    const {
        BuyCoinBal,
        SPOT_OPEN_ORDER_KINDS,
        SelectedCoin,
        SellCoinBal,
        cancelOrder,
        coinIconSrc,
        expandedRowIndex,
        fetchSpotWallets,
        formatOrderHistoryDateTime,
        formatSpotOpenOrderDateTime,
        formatTradeHistoryDateTime,
        getOpenOrderPair,
        getOpenOrderStopPrice,
        matchesOpenOrderKind,
        myTrades,
        nineDecimalFormat,
        onSpotOrderHistoryScrollViewport,
        onSpotTradeHistoryScrollViewport,
        openOrderKindTab,
        openOrders,
        orderHistoryExecutedValueNum,
        orderHistoryMarketLabel,
        orderHistoryQuoteCurrency,
        orderHistoryFeeCurrency,
        orderHistoryStatusClass,
        orderHistoryStatusLabel,
        spotOrderLifecycleStatus,
        orderType,
        pastOrderType,
        pastOrders,
        positionOrderTab,
        safeToFixed8,
        setExpandedRowIndex,
        setOpenOrderKindTab,
        setPositionOrderTab,
        setShowExecutedTrades,
        setTradeHistorySideFilter,
        setorderType,
        setpastOrderType,
        showExecutedTrades,
        spotHistoryViewportKey,
        spotOpenOrderFilledQty,
        spotOpenOrderRemainingQty,
        spotOpenOrderTifLabel,
        spotOpenOrderTypeLabel,
        spotWallets,
        token,
        tradeHistoryBaseAsset,
        tradeHistoryMarketLabel,
        tradeHistoryQuoteAsset,
        tradeHistoryRoleLabel,
        tradeHistorySideFilter,
        walletsLoading,
    } = props;

    const [cooldownIds, setCooldownIds] = useState(new Set());
    const startCooldown = useCallback((id) => {
        setCooldownIds((prev) => new Set([...prev, id]));
        setTimeout(() => {
            setCooldownIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
        }, 5000);
    }, []);

    const marketHideUntilRef = useRef(new Map());
    const [marketHideTick, setMarketHideTick] = useState(0);

    useEffect(() => {
        const openList = Array.isArray(openOrders) ? openOrders : [];
        const openIds = new Set(openList.map((o) => o?._id).filter(Boolean));
        const now = Date.now();
        const timers = [];

        openList.forEach((item) => {
            const id = item?._id;
            if (!id || !isSpotPlainMarketOrder(item)) return;
            if (marketHideUntilRef.current.has(id)) return;
            marketHideUntilRef.current.set(id, now + MARKET_OPEN_ORDER_HIDE_MS);
            timers.push(
                setTimeout(() => {
                    marketHideUntilRef.current.delete(id);
                    setMarketHideTick((t) => t + 1);
                }, MARKET_OPEN_ORDER_HIDE_MS)
            );
        });

        for (const id of [...marketHideUntilRef.current.keys()]) {
            if (!openIds.has(id)) {
                marketHideUntilRef.current.delete(id);
            }
        }

        return () => timers.forEach(clearTimeout);
    }, [openOrders]);

    const filteredOpenOrders = useMemo(() => {
        void marketHideTick;
        return (openOrders ?? [])
            .filter((item) => (orderType === item?.side || orderType === "All") && matchesOpenOrderKind(item, openOrderKindTab))
            .filter((item) => {
                if (!isSpotPlainMarketOrder(item)) return true;
                const id = item?._id;
                if (!id) return true;
                const until = marketHideUntilRef.current.get(id);
                if (until == null) return true;
                return Date.now() >= until;
            });
    }, [openOrders, orderType, openOrderKindTab, matchesOpenOrderKind, marketHideTick]);

    useEffect(() => {
        const el = document.querySelector('.trade_account_summary_assets');
        if (!el) return;

        const handleScroll = () => {
            const rect = el.getBoundingClientRect();
            // 80px is the top sticky offset. Add a 1px buffer to ensure it triggers correctly.
            if (rect.top <= 81) {
                el.classList.add('is-stuck');
            } else {
                el.classList.remove('is-stuck');
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <div className="trade_account_summary_assets">

                <div className="trade_summary_table_lft mt-0 position_order trade_summary_table_lft_position_order">
                    <div className="top_th_easyop border-0 spot_orders_top">
                        <SpotOrdersPrimaryTabsScroll positionOrderTab={positionOrderTab} setPositionOrderTab={setPositionOrderTab} />
                        {/* {positionOrderTab === "orderHistory" || positionOrderTab === "tradeHistory" ? (
                                        <div className="spot_orders_filter_actions px-2 py-1 justify-content-end">
                                            <Link
                                                className="spot_orders_reset"
                                                to={
                                                    positionOrderTab === "orderHistory"
                                                        ? "/user_profile/orders/spot_order_history"
                                                        : "/user_profile/orders/spot_position_history"
                                                }
                                            >
                                                View all
                                            </Link>
                                        </div>
                                    ) : null} */}
                    </div>

                    <div className={`cnt_table positions ${positionOrderTab === "positions" ? "active" : ""}`}>
                        {positionOrderTab === "positions" ? (
                            <SpotOpenOrdersFilterRow
                                SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                                openOrderKindTab={openOrderKindTab}
                                setOpenOrderKindTab={setOpenOrderKindTab}
                                orderType={orderType}
                                setorderType={setorderType}
                            />
                        ) : null}

                        <div className="desktop_view2">
                            <div className="table-responsive spot_orders_table_wrap" style={{ minHeight: "320px" }}>

                                <table className="table table_home spot_orders_table">
                                    <thead>
                                        <tr>
                                            <th>Market</th>
                                            <th>Time</th>
                                            <th>Side</th>
                                            <th>Type</th>
                                            <th>TIF</th>
                                            <th>Price</th>
                                            <th>Stop price</th>
                                            <th>Quantity</th>
                                            <th>Filled</th>
                                            <th>Remaining</th>
                                            <th>Avg price</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOpenOrders.length > 0 ? (
                                            filteredOpenOrders
                                                .map((item, index) => (
                                                    <tr key={item?._id || index}>
                                                        <td>{getOpenOrderPair(item)}</td>
                                                        <td>
                                                            <small>
                                                                <div className="c_view justify-content-start" >
                                                                    <span>{formatSpotOpenOrderDateTime(item, "DD/MM/YYYY")}{" "}
                                                                        <small>{formatSpotOpenOrderDateTime(item, "HH:mm:ss")}</small>
                                                                    </span>
                                                                </div>
                                                            </small>
                                                        </td>
                                                        <td className={spotSideColorClass(item?.side)}>{item?.side}</td>
                                                        <td>{spotOpenOrderTypeLabel(item)}</td>
                                                        <td>{spotOpenOrderTifLabel(item)}</td>
                                                        <td>{safeToFixed8(item?.price, "Market")}</td>
                                                        <td>{getOpenOrderStopPrice(item)}</td>
                                                        <td>{nineDecimalFormat(Number(item?.quantity ?? 0))}</td>
                                                        <td>{nineDecimalFormat(spotOpenOrderFilledQty(item))}</td>
                                                        <td>{nineDecimalFormat(spotOpenOrderRemainingQty(item))}</td>
                                                        <td>{safeToFixed8(item?.avg_execution_price, "0")}</td>
                                                        <td>
                                                            {(() => {
                                                                const st = spotOrderLifecycleStatus(item);
                                                                return st || "—";
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {cooldownIds.has(item?._id)
                                                                ? <span style={{ fontSize: 12, color: "#8e949e" }}>Cancelling…</span>
                                                                : <button className="btn text-danger btn-sm btn-icon" type="button" onClick={() => { startCooldown(item?._id); cancelOrder(item?._id); }}><i className="ri-delete-bin-6-line pr-0" /></button>
                                                            }
                                                        </td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr className="no-data-row spot_orders_no_data_row">
                                                <td colSpan={13}>
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                            </div>
                        </div>

                        <div className="order_history_mobile_view twomobile so-mobile">
                            <div className="d-flex datalist_mbl flex-column">
                                {filteredOpenOrders.length > 0 ? (
                                    filteredOpenOrders
                                        .map((item, index) => (
                                            <OrderMobileCardShell
                                                key={item?._id || `oo-${index}`}
                                                desktopColumnCount={TRADE_OPEN_ORDERS_DESKTOP_COLS}
                                                popupTitle="Open order details"
                                                heroPrimary={getOpenOrderPair(item)}
                                                heroSecondary={mobileHeroSideTypeSubtitle(item?.side, spotOpenOrderTypeLabel(item))}
                                                heroDatetime={`${formatSpotOpenOrderDateTime(item, "DD/MM/YYYY")} ${formatSpotOpenOrderDateTime(item, "HH:mm:ss")}`}
                                            >
                                                <div className="so-mobileCard__row">
                                                    <span>Date</span>
                                                    <span>{formatSpotOpenOrderDateTime(item, "DD/MM/YYYY")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Time</span>
                                                    <span>{formatSpotOpenOrderDateTime(item, "HH:mm:ss")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Market</span>
                                                    <span>{getOpenOrderPair(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Side</span>
                                                    <span className={spotSideColorClass(item?.side)}>{item?.side}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Type</span>
                                                    <span>{spotOpenOrderTypeLabel(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>TIF</span>
                                                    <span>{spotOpenOrderTifLabel(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Price</span>
                                                    <span className="so-num">{safeToFixed8(item?.price, "0")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Stop price</span>
                                                    <span className="so-num">{getOpenOrderStopPrice(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Quantity</span>
                                                    <span className="so-num">{nineDecimalFormat(Number(item?.quantity ?? 0))}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Filled</span>
                                                    <span className="so-num">{nineDecimalFormat(spotOpenOrderFilledQty(item))}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Remaining</span>
                                                    <span className="so-num">{nineDecimalFormat(spotOpenOrderRemainingQty(item))}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Avg price</span>
                                                    <span className="so-num">{safeToFixed8(item?.avg_execution_price, "0")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Status</span>
                                                    <span>{spotOrderLifecycleStatus(item) || "—"}</span>
                                                </div>
                                                <div className="so-mobileCard__actions">
                                                    {cooldownIds.has(item?._id)
                                                        ? <span style={{ fontSize: 12, color: "#8e949e" }}>Cancelling…</span>
                                                        : <button type="button" className="so-cancelBtn" onClick={() => { startCooldown(item?._id); cancelOrder(item?._id); }}>Cancel order</button>
                                                    }
                                                </div>
                                            </OrderMobileCardShell>
                                        ))
                                ) : (
                                    <div className="no-data-wrapper w-100">
                                        <div className="spot_orders_empty_state" role="status">
                                            <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    <div className={`cnt_table tradeHistory ${positionOrderTab === "tradeHistory" ? "active" : ""}`}>
                        {positionOrderTab === "tradeHistory" ? (
                            <FilterBarScrollable>
                                <div className="spot_orders_filter_chips">
                                    <div className="spot_orders_filter_actions">
                                        <CustomFilterDropdown
                                            options={[
                                                { value: "All", label: "All Sides" },
                                                { value: "BUY", label: "Buy" },
                                                { value: "SELL", label: "Sell" }
                                            ]}
                                            value={tradeHistorySideFilter}
                                            onChange={(val) => setTradeHistorySideFilter(val)}
                                            label="All Sides"
                                        />
                                        <button
                                            type="button"
                                            className="spot_orders_reset"
                                            onClick={() => setTradeHistorySideFilter("All")}
                                        >
                                            <i className="ri-refresh-line" aria-hidden="true" />
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </FilterBarScrollable>
                        ) : null}

                        <div className="desktop_view2">
                            <div
                                key={`spot-th-d-${spotHistoryViewportKey || "x"}`}
                                className="table-responsive spot_orders_table_wrap"
                                style={{ height: "353px", minHeight: "320px", overflowY: "auto" }}
                                onScroll={
                                    positionOrderTab === "tradeHistory"
                                        ? onSpotTradeHistoryScrollViewport
                                        : undefined
                                }
                            >
                                {(() => {
                                    const filtered = (myTrades || []).filter(
                                        (item) =>
                                            tradeHistorySideFilter === "All" ||
                                            item?.side === tradeHistorySideFilter
                                    );
                                    if (!filtered.length) {
                                        return (
                                            <div className="spot_orders_empty_state" role="status">
                                                <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                            </div>
                                        );
                                    }
                                    return (
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Pair</th>
                                                    <th>Side</th>
                                                    <th>Role</th>
                                                    <th>Price</th>
                                                    <th>Quantity</th>
                                                    <th>Value</th>
                                                    <th>Fee</th>
                                                    {/* <th>TDS</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.map((item, index) => {
                                                    const baseCc = tradeHistoryBaseAsset(item);
                                                    const quoteCc = tradeHistoryQuoteAsset(item);
                                                    const sideClass = spotSideColorClass(item?.side);
                                                    return (
                                                        <tr key={item?._id || index}>
                                                            <td>
                                                                <small>
                                                                    <div className="c_view justify-content-start">
                                                                        <span>
                                                                            {formatTradeHistoryDateTime(item, "DD/MM/YYYY")}{" "}
                                                                            <small>{formatTradeHistoryDateTime(item, "HH:mm:ss")}</small>
                                                                        </span>
                                                                    </div>
                                                                </small>
                                                            </td>
                                                            <td>{tradeHistoryMarketLabel(item)}</td>
                                                            <td className={sideClass}>{item?.side || "—"}</td>
                                                            <td>{tradeHistoryRoleLabel(item)}</td>
                                                            <td>{safeToFixed8(item?.price, "0")}</td>
                                                            <td>
                                                                {safeToFixed8(item?.quantity, "0")}
                                                                {baseCc ? ` ${baseCc}` : ""}
                                                            </td>
                                                            <td>
                                                                {safeToFixed8(item?.quote_quantity, "0")}
                                                                {quoteCc ? ` ${quoteCc}` : ""}
                                                            </td>
                                                            <td>
                                                                {safeToFixed8(item?.fee, "0")}
                                                                {item?.fee_asset ? ` ${item.fee_asset}` : ""}
                                                            </td>
                                                            {/* <td>
                                                                {safeToFixed8(item?.tds, "0")}
                                                                {item?.tds_asset ? ` ${item.tds_asset}` : ""}
                                                            </td> */}

                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    );
                                })()}
                            </div>
                        </div>

                        <div
                            key={`spot-th-m-${spotHistoryViewportKey || "x"}`}
                            className="order_history_mobile_view twomobile so-mobile"
                            style={{ maxHeight: "353px", overflowY: "auto" }}
                            onScroll={
                                positionOrderTab === "tradeHistory"
                                    ? onSpotTradeHistoryScrollViewport
                                    : undefined
                            }
                        >
                            <div className="d-flex datalist_mbl flex-column">
                                {(() => {
                                    const filtered = (myTrades || []).filter(
                                        (item) =>
                                            tradeHistorySideFilter === "All" ||
                                            item?.side === tradeHistorySideFilter
                                    );
                                    if (!filtered.length) {
                                        return (
                                            <div className="no-data-wrapper w-100">
                                                <div className="spot_orders_empty_state" role="status">
                                                    <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                </div>
                                            </div>
                                        );
                                    }
                                    return filtered.map((item, index) => {
                                        const baseCc = tradeHistoryBaseAsset(item);
                                        const quoteCc = tradeHistoryQuoteAsset(item);
                                        return (
                                            <OrderMobileCardShell
                                                key={item?._id || `th-${index}`}
                                                desktopColumnCount={TRADE_TRADE_HISTORY_DESKTOP_COLS}
                                                popupTitle="Trade details"
                                                heroPrimary={tradeHistoryMarketLabel(item)}
                                                heroSecondary={mobileHeroSideTypeSubtitle(item?.side, tradeHistoryRoleLabel(item))}
                                                heroDatetime={`${formatTradeHistoryDateTime(item, "DD/MM/YYYY")} ${formatTradeHistoryDateTime(item, "HH:mm:ss")}`}
                                            >
                                                <div className="so-mobileCard__row">
                                                    <span>Date</span>
                                                    <span>{formatTradeHistoryDateTime(item, "DD/MM/YYYY")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Time</span>
                                                    <span>{formatTradeHistoryDateTime(item, "HH:mm:ss")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Pair</span>
                                                    <span>{tradeHistoryMarketLabel(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Side</span>
                                                    <span className={spotSideColorClass(item?.side)}>
                                                        {item?.side || "—"}
                                                    </span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Role</span>
                                                    <span>{tradeHistoryRoleLabel(item)}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Price</span>
                                                    <span className="so-num">{safeToFixed8(item?.price, "0")}</span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Quantity</span>
                                                    <span className="so-num">
                                                        {safeToFixed8(item?.quantity, "0")}
                                                        {baseCc ? ` ${baseCc}` : ""}
                                                    </span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Value</span>
                                                    <span className="so-num">
                                                        {safeToFixed8(item?.quote_quantity, "0")}
                                                        {quoteCc ? ` ${quoteCc}` : ""}
                                                    </span>
                                                </div>
                                                <div className="so-mobileCard__row">
                                                    <span>Fee</span>
                                                    <span className="so-num">
                                                        {safeToFixed8(item?.fee, "0")}
                                                        {item?.fee_asset ? ` ${item.fee_asset}` : ""}
                                                    </span>
                                                </div>
                                                {/* <div className="so-mobileCard__row">
                                                    <span>TDS</span>
                                                    <span className="so-num">
                                                        {safeToFixed8(item?.tds, "0")}
                                                        {item?.tds_asset ? ` ${item.tds_asset}` : ""}
                                                    </span>
                                                </div> */}
                                            </OrderMobileCardShell>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className={`cnt_table loan ${positionOrderTab === "loan" ? "active" : ""}`}>
                        <div className="desktop_view2">
                            <div className="table-responsive spot_orders_table_wrap spot_orders_placeholder_shell">
                                <div className="spot_orders_empty_state" role="status">
                                    <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                    {/* <p className="spot_orders_empty_caption">No data</p> */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`cnt_table bots ${positionOrderTab === "bots" ? "active" : ""}`}>
                        <div className="desktop_view2">
                            <div className="table-responsive spot_orders_table_wrap spot_orders_placeholder_shell">
                                <div className="spot_orders_empty_state" role="status">
                                    <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                    {/* <p className="spot_orders_empty_caption">No data</p> */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`cnt_table orderHistory ${positionOrderTab === "orderHistory" ? "active" : ""}`}>

                        <div className="desktop_view2">
                            <div
                                key={`spot-oh-d-${spotHistoryViewportKey || "x"}`}
                                className="table-responsive"
                                style={{ height: "353px", overflowY: "auto" }}
                                onScroll={
                                    positionOrderTab === "orderHistory"
                                        ? onSpotOrderHistoryScrollViewport
                                        : undefined
                                }
                            >
                                <table className="table table_home ">
                                    <thead>
                                        <tr>
                                            <th>Time</th>
                                            <th>Market</th>
                                            <th>
                                                <TableHeaderSideSelect
                                                    value={pastOrderType}
                                                    onChange={(val) => setpastOrderType(val)}
                                                />
                                            </th>
                                            <th>Type</th>
                                            <th>TIF</th>
                                            <th>Price</th>
                                            <th>Avg</th>
                                            <th>Qty</th>
                                            <th>Filled</th>
                                            <th>Remaining</th>
                                            <th>Fill %</th>
                                            <th>Value</th>
                                            <th>Fee</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pastOrders?.length > 0 ? (
                                            pastOrders.map((item, index) => {
                                                const quoteCc = orderHistoryQuoteCurrency(item);
                                                const feeCc = orderHistoryFeeCurrency(item);
                                                const [baseSym] = orderHistoryMarketLabel(item).split("/");
                                                const execVal = orderHistoryExecutedValueNum(item);
                                                const lifeStatus = spotOrderLifecycleStatus(item);
                                                const stLabel = orderHistoryStatusLabel(lifeStatus);
                                                const stClass = orderHistoryStatusClass(lifeStatus);
                                                return (
                                                    <React.Fragment key={item?._id || `oh-${index}`}>
                                                        <tr
                                                            onClick={() =>
                                                                setExpandedRowIndex(expandedRowIndex === index ? null : index)
                                                            }
                                                            className="cursor-pointer"
                                                        >
                                                            <td>
                                                                <div className="c_view justify-content-start">
                                                                    {item?.executed_prices?.length > 0 && (
                                                                        <p className="ms-2 mx-2 text-xl d-inline text-success">
                                                                            {expandedRowIndex === index ? "▾" : "▸"}
                                                                        </p>
                                                                    )}
                                                                    <span>
                                                                        {formatOrderHistoryDateTime(item, "DD/MM/YYYY")}{" "}
                                                                        <small>{formatOrderHistoryDateTime(item, "HH:mm:ss")}</small>
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{orderHistoryMarketLabel(item)}</td>
                                                            <td className={spotSideColorClass(item?.side)}>{item?.side}</td>
                                                            <td>{spotOpenOrderTypeLabel(item)}</td>
                                                            <td>{spotOpenOrderTifLabel(item)}</td>
                                                            <td>{orderHistoryLimitOrMarketPrice(item, safeToFixed8)}</td>
                                                            <td>{orderHistoryAvgFillPrice(item, safeToFixed8, spotOpenOrderFilledQty)}</td>
                                                            <td>{safeToFixed8(item?.quantity, "0.00")}</td>
                                                            <td>{safeToFixed8(spotOpenOrderFilledQty(item), "0.00")}</td>
                                                            <td>{safeToFixed8(spotOpenOrderRemainingQty(item), "0.00")}</td>
                                                            <td>
                                                                {(() => {
                                                                    const fp = item?.fill_percent;
                                                                    if (fp == null || String(fp).trim() === "") return "—";
                                                                    const s = String(fp).trim();
                                                                    return s.endsWith("%") ? s : `${s}%`;
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {execVal != null && Number.isFinite(execVal)
                                                                    ? safeToFixed8(execVal, "0")
                                                                    : "—"}
                                                            </td>
                                                            <td>
                                                                {safeToFixed8(item?.total_fee, "0")}
                                                                {feeCc ? ` ${feeCc}` : ""}
                                                            </td>
                                                            <td className={`text-${stClass}`}>
                                                                <span className="d-block">{stLabel}</span>
                                                                {item?.reject_reason ? (
                                                                    <small className="text-muted d-block">{item.reject_reason}</small>
                                                                ) : null}
                                                            </td>
                                                        </tr>
                                                        {expandedRowIndex === index && item?.executed_prices?.length > 0 ? (
                                                            <tr>
                                                                <td colSpan={15}>
                                                                    <div className="table-responsive bg-dark">
                                                                        <table className="table table_home   ">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Date</th>
                                                                                    <th>Price</th>
                                                                                    <th>Executed</th>
                                                                                    <th>Transaction Fee</th>
                                                                                    <th>Total</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {aggregateOrderHistoryExecutedLegs(item).map((leg) => (
                                                                                    <tr key={leg.reactKey}>
                                                                                        <td>
                                                                                            {formatExecutionLegDateTime(leg, "DD/MM/YYYY")}{" "}
                                                                                            <small>{formatExecutionLegDateTime(leg, "HH:mm:ss")}</small>
                                                                                        </td>
                                                                                        <td>
                                                                                            {safeToFixed8(leg.price, "0")}{" "}
                                                                                            {quoteCc}
                                                                                        </td>
                                                                                        <td>
                                                                                            {safeToFixed8(leg.quantity, "0")}{" "}
                                                                                            {baseSym}
                                                                                        </td>
                                                                                        <td>
                                                                                            {safeToFixed8(leg.fee, "0")}{" "}
                                                                                            {quoteCc}
                                                                                        </td>
                                                                                        <td>
                                                                                            {safeToFixed8(
                                                                                                Number(leg.price) * Number(leg.quantity),
                                                                                                "0"
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ) : null}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <tr className="no-data-row spot_orders_no_data_row">
                                                <td colSpan={15}>
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img
                                                            src="/images/no-data.svg"
                                                            alt=""
                                                            width={120}
                                                            height={144}
                                                            className="spot_orders_empty_telescope"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                            </div>
                        </div>
                        <div
                            key={`spot-oh-m-${spotHistoryViewportKey || "x"}`}
                            className="order_history_mobile_view twomobile so-mobile"
                            style={{ maxHeight: "353px", overflowY: "auto" }}
                            onScroll={
                                positionOrderTab === "orderHistory"
                                    ? onSpotOrderHistoryScrollViewport
                                    : undefined
                            }
                        >
                            <div className="d-flex datalist_mbl flex-column">
                                {pastOrders?.length > 0 ? (
                                    pastOrders
                                        .filter((item) => item?.side === pastOrderType || pastOrderType === "All")
                                        .map((item, index) => {
                                            const quoteCc = orderHistoryQuoteCurrency(item);
                                            const [baseSym] = orderHistoryMarketLabel(item).split("/");
                                            const execVal = orderHistoryExecutedValueNum(item);
                                            const lifeStatus = spotOrderLifecycleStatus(item);
                                            const stLabel = orderHistoryStatusLabel(lifeStatus);
                                            const stClass = orderHistoryStatusClass(lifeStatus);
                                            const aggregatedLegs = aggregateOrderHistoryExecutedLegs(item);
                                            return (
                                                <React.Fragment key={item?._id || index}>
                                                    <OrderMobileCardShell
                                                        desktopColumnCount={TRADE_ORDER_HISTORY_DESKTOP_COLS}
                                                        popupTitle="Order details"
                                                        heroPrimary={orderHistoryMarketLabel(item)}
                                                        heroSecondary={mobileHeroSideTypeSubtitle(item?.side, spotOpenOrderTypeLabel(item))}
                                                        heroDatetime={`${formatOrderHistoryDateTime(item, "DD/MM/YYYY")} ${formatOrderHistoryDateTime(item, "HH:mm:ss")}`}
                                                    >
                                                        <div className="so-mobileCard__row">
                                                            <span>Time</span>
                                                            <span>{formatOrderHistoryDateTime(item, "HH:mm:ss")}</span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Market</span>
                                                            <span>{orderHistoryMarketLabel(item)}</span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Type</span>
                                                            <span>{spotOpenOrderTypeLabel(item)}</span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>TIF</span>
                                                            <span>{spotOpenOrderTifLabel(item)}</span>
                                                        </div>
                                                      
                                                        <div className="so-mobileCard__row">
                                                            <span>Avg Price</span>
                                                            <span className="so-num">
                                                                {orderHistoryAvgFillPrice(item, safeToFixed8, spotOpenOrderFilledQty)}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Quantity</span>
                                                            <span className="so-num">{safeToFixed8(item?.quantity, "0")}</span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Filled</span>
                                                            <span className="so-num">
                                                                {safeToFixed8(spotOpenOrderFilledQty(item), "0")}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Remaining</span>
                                                            <span className="so-num">
                                                                {safeToFixed8(spotOpenOrderRemainingQty(item), "0")}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Fill %</span>
                                                            <span>
                                                                {(() => {
                                                                    const fp = item?.fill_percent;
                                                                    if (fp == null || String(fp).trim() === "") return "—";
                                                                    const s = String(fp).trim();
                                                                    return s.endsWith("%") ? s : `${s}%`;
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Value</span>
                                                            <span className="so-num">
                                                                {execVal != null && Number.isFinite(execVal)
                                                                    ? safeToFixed8(execVal, "0")
                                                                    : "—"}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Fee</span>
                                                            <span className="so-num">
                                                                {safeToFixed8(item?.total_fee, "0")}
                                                                {quoteCc ? ` ${quoteCc}` : ""}
                                                            </span>
                                                        </div>
                                                        {/* <div className="so-mobileCard__row">
                                                                        <span>TDS</span>
                                                                        <span className="so-num">{safeToFixed8(item?.total_tds, "0")}</span>
                                                                    </div> */}
                                                        <div className="so-mobileCard__row">
                                                            <span>Status</span>
                                                            <span className={`text-${stClass}`}>{stLabel}</span>
                                                        </div>
                                                        {item?.reject_reason ? (
                                                            <div className="so-mobileCard__row">
                                                                <span>Note</span>
                                                                <span>{item.reject_reason}</span>
                                                            </div>
                                                        ) : null}
                                                    </OrderMobileCardShell>

                                                    {item?.executed_prices?.length > 0 ? (
                                                        <div className="order_datalist trade_page_mob_execBelow">
                                                            <div
                                                                className={`executed_trades_list ${showExecutedTrades[item?._id] ? "active" : ""}`}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setShowExecutedTrades({
                                                                            ...showExecutedTrades,
                                                                            [item?._id]: !showExecutedTrades[item?._id],
                                                                        })
                                                                    }
                                                                >
                                                                    <i
                                                                        className={`ri-arrow-drop-down-line ${showExecutedTrades[item?._id] ? "rotated" : ""}`}
                                                                    />{" "}
                                                                    Executed trades
                                                                </button>
                                                                {showExecutedTrades[item?._id] ? (
                                                                    <div className="executed_trades_list_items">
                                                                        {aggregatedLegs.map((leg) => (
                                                                            <ul key={leg.reactKey}>
                                                                                <li>
                                                                                    {formatExecutionLegDateTime(leg, "DD/MM/YYYY HH:mm:ss")}{" "}
                                                                                   
                                                                                </li>
                                                                                <li>
                                                                                    Price:{" "}
                                                                                    <span>
                                                                                        {safeToFixed8(leg.price, "0")} {quoteCc}
                                                                                    </span>
                                                                                </li>
                                                                                <li>
                                                                                    Executed:{" "}
                                                                                    <span>
                                                                                        {safeToFixed8(leg.quantity, "0")} {baseSym}
                                                                                    </span>
                                                                                </li>
                                                                                <li>
                                                                                    Transaction Fee:{" "}
                                                                                    <span>
                                                                                        {safeToFixed8(leg.fee, "0")} {quoteCc}
                                                                                    </span>
                                                                                </li>
                                                                                <li>
                                                                                    Total:{" "}
                                                                                    <span>
                                                                                        {safeToFixed8(
                                                                                            Number(leg.price) * Number(leg.quantity),
                                                                                            "0"
                                                                                        )}
                                                                                    </span>
                                                                                </li>
                                                                            </ul>
                                                                        ))}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })
                                ) : (
                                    <div className="no-data-wrapper w-100 d-flex justify-content-center py-4">
                                        <div className="spot_orders_empty_state" role="status">
                                            <img
                                                src="/images/no-data.svg"
                                                alt=""
                                                width={120}
                                                height={144}
                                                className="spot_orders_empty_telescope"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>


                    </div>

                </div>

                <div className="assets_right d-none d-lg-block assets_panel_desktop">
                    <div id="tab_4" className="assets_panel_inner">
                        <div className="assets_panel_header">
                            <h5 className="assets_panel_title">Assets</h5>
                            <button type="button" className="assets_panel_refresh" onClick={() => fetchSpotWallets()} aria-label="Refresh balances">
                                <i className="ri-refresh-line" />
                            </button>
                        </div>

                        {/* <div className="assets_balance_rows">
                            <div className="assets_balance_row">
                                <span className="assets_balance_label">{SelectedCoin?.quote_currency || "USDT"} Balance</span>
                                <span className="assets_balance_val">
                                    {token
                                        ? `${BuyCoinBal !== undefined && BuyCoinBal !== null ? parseFloat(BuyCoinBal).toFixed(8).replace(/\.?0+$/, "") : "0"} ${SelectedCoin?.quote_currency || "USDT"}`
                                        : `-- ${SelectedCoin?.quote_currency || "USDT"}`}
                                </span>
                            </div>
                            <div className="assets_balance_row">
                                <span className="assets_balance_label">{SelectedCoin?.base_currency || "BTC"} Balance</span>
                                <span className="assets_balance_val">
                                    {token
                                        ? `${SellCoinBal !== undefined && SellCoinBal !== null ? parseFloat(SellCoinBal).toFixed(8).replace(/\.?0+$/, "") : "0"} ${SelectedCoin?.base_currency || "BTC"}`
                                        : `-- ${SelectedCoin?.base_currency || "BTC"}`}
                                </span>
                            </div>
                        </div>

                        <div className="assets_panel_actions">
                            <Link className="assets_panel_pill" to={token ? "/asset_managemnet/deposit" : "/login"}>
                                Deposit
                            </Link>
                            <Link className="assets_panel_pill" to={token ? "/convert" : "/login"}>
                                Convert
                            </Link>
                            <Link className="assets_panel_pill" to={token ? "/user_profile/asset_overview" : "/login"}>
                                Transfer
                            </Link>
                        </div> */}

                        <div className="assets_panel_list_card">
                            {!token ? (
                                <div className="assets_panel_login_hint">
                                    <p className="assets_panel_login_text mb-2">Please login to view your wallets</p>
                                    <Link to="/login" className="btn btn-primary btn-sm">Login</Link>
                                </div>
                            ) : walletsLoading ? (
                                <div className="assets_panel_loading">
                                    <ThemeAwareSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="assets_panel_list_head">
                                        <span>Asset</span>
                                        <span>Balance</span>
                                    </div>
                                    <ul className="assets_panel_asset_rows">
                                        {spotWallets?.length > 0 ? (
                                            spotWallets.map((wallet, index) => (
                                                <li key={wallet?._id || index} className="assets_panel_asset_row">
                                                    <div className="assets_panel_asset_left">
                                                        <img
                                                            src={coinIconSrc(wallet?.icon_path)}
                                                            alt={wallet?.short_name || ""}
                                                            width="32"
                                                            height="32"
                                                            className="assets_panel_coin_icon"
                                                            onError={(e) => {
                                                                e.target.src = "/images/coin_placeholder.png";
                                                            }}
                                                        />
                                                        <div className="assets_panel_asset_meta">
                                                            <span className="assets_panel_sym">{wallet?.short_name}</span>
                                                            <span className="assets_panel_full">{wallet?.currency || wallet?.short_name}</span>
                                                        </div>
                                                    </div>
                                                    <span className="assets_panel_bal">
                                                        {parseFloat((wallet?.balance || 0).toFixed(8))}
                                                    </span>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="assets_panel_empty">
                                                <span className="text-muted">No assets in spot wallet</span>
                                            </li>
                                        )}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>

            </div>

        </>
    );
};

export default memo(TradeHistorySection);
